<template>
    <div>
      <v-app-bar
        app
        :color="scrolled ? 'rgba(26, 46, 38, 0.97)' : 'rgba(45, 74, 62, 0.95)'"
        :elevation="scrolled ? 4 : 2"
        class="app-bar-custom"
        dark
        height="80"
        style="transition: all 0.3s ease;"
      >
        <v-container class="px-8">
          <v-row align="center" class="mx-0">
            <!-- Logo -->
            <v-col cols="auto" class="pa-0">
              <router-link :to="user ? '/dashboard' : '/'" style="text-decoration: none;">
                <div class="d-flex align-center">
                  <div class="logo-container">
                    <v-icon size="40" color="gold" class="crown-icon">mdi-crown</v-icon>
                    <span class="logo-text">ClubLink</span>
                    <div class="logo-subtitle">SOFTWARE</div>
                  </div>
                </div>
              </router-link>
            </v-col>
  
            <v-spacer></v-spacer>
  
            <!-- Desktop Navigation - Public -->
            <v-col cols="auto" class="hidden-sm-and-down pa-0" v-if="!user">
              <v-btn
                v-for="item in navItems"
                :key="item.title"
                :to="item.route"
                text
                class="nav-link mx-2"
                :ripple="false"
              >
                {{ item.title }}
              </v-btn>
            </v-col>

            <!-- Desktop Navigation - Authenticated -->
            <v-col cols="auto" class="hidden-sm-and-down pa-0" v-if="user">
              <v-chip class="mx-2" color="rgba(212, 175, 55, 0.2)" text-color="gold">
                <v-icon left small>mdi-account-circle</v-icon>
                {{ userEmail }}
              </v-chip>
            </v-col>
  
            <!-- CTA Buttons - Public -->
            <v-col cols="auto" class="hidden-sm-and-down pa-0 ml-4" v-if="!user">
              <v-btn
                to="/login"
                outlined
                color="gold"
                class="mr-3 login-btn"
                :ripple="false"
              >
                Login
              </v-btn>
              <v-btn
                to="/demo"
                color="gold"
                class="demo-btn"
                elevation="0"
                :ripple="false"
              >
                Request Demo
              </v-btn>
            </v-col>

            <!-- CTA Buttons - Authenticated -->
            <v-col cols="auto" class="hidden-sm-and-down pa-0 ml-4" v-if="user">
              <v-btn
                @click="logout"
                color="gold"
                class="demo-btn"
                elevation="0"
                :ripple="false"
              >
                <v-icon left small>mdi-logout</v-icon>
                Logout
              </v-btn>
            </v-col>
  
            <!-- Mobile Menu -->
            <v-col cols="auto" class="hidden-md-and-up pa-0">
              <v-app-bar-nav-icon @click="drawer = !drawer" color="gold"></v-app-bar-nav-icon>
            </v-col>
          </v-row>
        </v-container>
      </v-app-bar>
  
      <!-- Mobile Drawer -->
      <v-navigation-drawer
        v-model="drawer"
        app
        temporary
        right
        dark
        color="primary"
        width="280"
      >
        <v-list class="pt-8">
          <!-- Public Navigation -->
          <template v-if="!user">
            <v-list-item
              v-for="item in navItems"
              :key="item.title"
              :to="item.route"
              link
            >
              <v-list-item-content>
                <v-list-item-title class="mobile-nav-item">
                  {{ item.title }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            
            <v-divider class="my-4" dark></v-divider>
            
            <v-list-item to="/login" link>
              <v-list-item-content>
                <v-list-item-title class="mobile-nav-item">Login</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            
            <v-list-item to="/demo" link>
              <v-list-item-content>
                <v-list-item-title class="mobile-nav-item gold--text">
                  Request Demo
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>

          <!-- Authenticated Navigation -->
          <template v-else>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-subtitle class="mb-1">Signed in as</v-list-item-subtitle>
                <v-list-item-title class="gold--text">{{ userEmail }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-divider class="my-4" dark></v-divider>

            <v-list-item @click="logout" link>
              <v-list-item-icon>
                <v-icon color="gold">mdi-logout</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="mobile-nav-item gold--text">Logout</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-list>
      </v-navigation-drawer>
    </div>
  </template>
  
  <script>
  import { auth, db } from '../firebase'
  import { doc, getDoc } from 'firebase/firestore'

  export default {
    name: 'AppNavigation',
    data() {
      return {
        scrolled: false,
        drawer: false,
        user: null,
        userEmail: '',
        userRole: '',
        navItems: [
          { title: 'Home', route: '/' },
          { title: 'Why ClubLink', route: '/why' },
          { title: 'Services', route: '/services' },
          { title: 'About', route: '/about' },
          { title: 'Blog', route: '/blog' },
        ]
      };
    },
    mounted() {
      window.addEventListener('scroll', this.handleScroll);
      
      // Listen to auth state changes
      auth.onAuthStateChanged(async (user) => {
        this.user = user;
        if (user) {
          this.userEmail = user.email || '';
          // Fetch user role from Firestore
          try {
            const userDoc = await getDoc(doc(db, 'users', user.uid));
            if (userDoc.exists()) {
              this.userRole = userDoc.data().role || '';
            }
          } catch (error) {
            console.error('Error fetching user role:', error);
          }
        } else {
          this.userEmail = '';
          this.userRole = '';
        }
      });
    },
    beforeDestroy() {
      window.removeEventListener('scroll', this.handleScroll);
    },
    methods: {
      handleScroll() {
        this.scrolled = window.scrollY > 50;
      },
      async logout() {
        try {
          await auth.signOut();
          // Only navigate if not already on home page
          if (this.$route.path !== '/') {
            this.$router.push('/').catch(() => {});
          }
        } catch (error) {
          console.error('Logout error:', error);
        }
      }
    }
  };
  </script>
  
  <style scoped>
  .app-bar-custom {
    backdrop-filter: blur(10px);
    border-bottom: 1px solid rgba(212, 175, 55, 0.1);
  }
  
  .logo-container {
    position: relative;
    padding: 8px 0;
  }
  
  .crown-icon {
    position: absolute;
    top: -8px;
    left: 50%;
    transform: translateX(-50%);
    filter: drop-shadow(0 2px 4px rgba(212, 175, 55, 0.3));
  }
  
  .logo-text {
    font-family: 'Playfair Display', serif;
    font-size: 28px;
    font-weight: 700;
    color: #d4af37;
    letter-spacing: 2px;
    text-shadow: 0 2px 8px rgba(212, 175, 55, 0.3);
    display: block;
    margin-top: 20px;
  }
  
  .logo-subtitle {
    font-family: 'Montserrat', sans-serif;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 4px;
    color: #c9a961;
    text-align: center;
    margin-top: -4px;
  }
  
  .nav-link {
    font-family: 'Montserrat', sans-serif;
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: white !important;
    transition: all 0.3s ease;
  }
  
  .nav-link::before {
    background-color: transparent !important;
  }
  
  .nav-link:hover {
    color: #d4af37 !important;
    transform: translateY(-2px);
  }
  
  .nav-link.v-btn--active {
    color: #d4af37 !important;
  }
  
  .login-btn {
    font-family: 'Montserrat', sans-serif;
    font-weight: 600;
    letter-spacing: 1px;
    border: 2px solid #d4af37;
    transition: all 0.3s ease;
  }
  
  .login-btn:hover {
    background-color: rgba(212, 175, 55, 0.1);
    transform: translateY(-2px);
  }
  
  .demo-btn {
    font-family: 'Montserrat', sans-serif;
    font-weight: 600;
    letter-spacing: 1px;
    color: #2d4a3e !important;
    transition: all 0.3s ease;
    box-shadow: 0 4px 12px rgba(212, 175, 55, 0.4) !important;
  }
  
  .demo-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(212, 175, 55, 0.6) !important;
  }
  
  .mobile-nav-item {
    font-family: 'Montserrat', sans-serif;
    font-size: 16px;
    font-weight: 500;
    letter-spacing: 1px;
  }
  </style>